import { useEffect, useState } from 'react'
import { ArrowLeft, Contact, Trash2 } from 'lucide-react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'

import { CustomerFormFields } from '@/components/masters/CustomerFormFields'
import { CustomerPackagesTab } from '@/components/masters/CustomerPackagesTab'
import { CustomerVisitHistoryTab } from '@/components/masters/CustomerVisitHistoryTab'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/contexts/AuthContext'
import { useBranch } from '@/contexts/BranchContext'
import { api } from '@/lib/api'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import {
  createCustomer,
  deleteCustomer,
  fetchCustomer,
  updateCustomer,
} from '@/lib/api/customers'
import { fetchStylists } from '@/lib/api/stylists'
import {
  customerFormToPayload,
  customerToForm,
  emptyCustomerForm,
  getCustomerFieldError,
} from '@/lib/customers/customerForm'
import { formatLocalPhoneInput } from '@/lib/phone'
import { hasEffectivePermission, PERMISSIONS } from '@/lib/permissions'
import { SALON_DESK_MASTERS } from '@/lib/salonDeskConfig'
import { cn } from '@/lib/utils'

const CUSTOMER_TABS = [
  { id: 'profile', label: 'Profile' },
  { id: 'visits', label: 'Visit history' },
  ...(SALON_DESK_MASTERS.showCustomerPackagesTab
    ? [{ id: 'packages', label: 'Packages' }]
    : []),
  ...(SALON_DESK_MASTERS.showCustomerPhotos ? [{ id: 'photos', label: 'Photos' }] : []),
  ...(SALON_DESK_MASTERS.showCustomerRecommendations
    ? [{ id: 'recommendations', label: 'Recommendations' }]
    : []),
]

/**
 * @param {{ mode: 'create' | 'edit' }} props
 */
export function CustomerFormPage({ mode }) {
  const navigate = useNavigate()
  const location = useLocation()
  const { customerId } = useParams()
  const { user } = useAuth()
  const { activeBranchId } = useBranch()
  const creating = mode === 'create'
  const canCreate = hasEffectivePermission(user, PERMISSIONS.CUSTOMERS_CREATE)
  const canEdit = hasEffectivePermission(user, PERMISSIONS.CUSTOMERS_EDIT)
  const canDelete = hasEffectivePermission(user, PERMISSIONS.CUSTOMERS_DELETE)

  const [stylists, setStylists] = useState([])
  const [form, setForm] = useState(emptyCustomerForm())
  const [activeTab, setActiveTab] = useState('profile')
  const [loading, setLoading] = useState(!creating)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(() => location.state?.success ?? '')
  const [photos, setPhotos] = useState([])
  const [photosLoading, setPhotosLoading] = useState(false)
  const [recommendations, setRecommendations] = useState([])
  const [recommendationsLoading, setRecommendationsLoading] = useState(false)
  const [photoCaption, setPhotoCaption] = useState('')
  const selectedId = creating ? null : Number(customerId)

  useEffect(() => {
    if (location.state?.success) {
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.pathname, location.state, navigate])

  useEffect(() => {
    if (creating && !canCreate) {
      navigate('/customers', { replace: true })
    }
    if (!creating && !canEdit) {
      navigate('/customers', { replace: true })
    }
  }, [canCreate, canEdit, creating, navigate])

  useEffect(() => {
    if (!SALON_DESK_MASTERS.showCustomerPreferredStylist) {
      return undefined
    }

    let active = true

    async function loadStylists() {
      try {
        const items = await fetchStylists()
        if (active) setStylists(items.filter((item) => item.is_active))
      } catch {
        if (active) setStylists([])
      }
    }

    loadStylists()
    return () => {
      active = false
    }
  }, [activeBranchId])

  useEffect(() => {
    if (creating || !selectedId) {
      return undefined
    }

    let cancelled = false
    setLoading(true)
    setError('')

    async function loadCustomer() {
      try {
        const customer = await fetchCustomer(selectedId)
        if (!cancelled) {
          setForm(customerToForm(customer))
        }
      } catch (err) {
        if (!cancelled) {
          setError(apiErrorMessage(err, 'Unable to load customer.'))
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    loadCustomer()
    return () => {
      cancelled = true
    }
  }, [creating, selectedId])

  useEffect(() => {
    if (creating || !selectedId) return undefined
    let cancelled = false

    async function loadExtras() {
      if (SALON_DESK_MASTERS.showCustomerPhotos && activeTab === 'photos') {
        setPhotosLoading(true)
        try {
          const { data } = await api.get(`/crm/customers/${selectedId}/photos`)
          if (!cancelled) setPhotos(data?.data?.photos ?? [])
        } catch (err) {
          if (!cancelled) {
            setError(apiErrorMessage(err, 'Unable to load photos.'))
            setPhotos([])
          }
        } finally {
          if (!cancelled) setPhotosLoading(false)
        }
      }

      if (SALON_DESK_MASTERS.showCustomerRecommendations && activeTab === 'recommendations') {
        setRecommendationsLoading(true)
        try {
          const { data } = await api.get(`/crm/customers/${selectedId}/recommendations`)
          if (!cancelled) {
            setRecommendations(data?.data?.last_services ?? [])
          }
        } catch (err) {
          if (!cancelled) {
            setError(apiErrorMessage(err, 'Unable to load recommendations.'))
            setRecommendations([])
          }
        } finally {
          if (!cancelled) setRecommendationsLoading(false)
        }
      }
    }

    void loadExtras()
    return () => {
      cancelled = true
    }
  }, [activeTab, creating, selectedId])

  function updateField(field, value) {
    setForm((current) => ({ ...current, [field]: value }))
    setSuccess('')
  }

  function updatePhoneCountry(countryCode) {
    setForm((current) => ({
      ...current,
      phoneCountry: countryCode,
      phoneLocal: formatLocalPhoneInput(current.phoneLocal, countryCode),
    }))
    setSuccess('')
  }

  async function handleSubmit(event) {
    event.preventDefault()
    setSubmitting(true)
    setError('')
    setSuccess('')

    const payload = customerFormToPayload(form, { includeBlacklist: !creating })

    try {
      if (creating) {
        const created = await createCustomer(payload)
        navigate(`/customers/${created.id}/edit`, {
          replace: true,
          state: { success: 'Customer created.' },
        })
        return
      }

      if (selectedId) {
        await updateCustomer(selectedId, payload)
        setSuccess('Customer saved.')
      }
    } catch (err) {
      const message =
        getCustomerFieldError(err, 'phone') ??
        getCustomerFieldError(err, 'name') ??
        getCustomerFieldError(err, 'email') ??
        err?.response?.data?.message ??
        'Unable to save customer.'
      setError(message)
    } finally {
      setSubmitting(false)
    }
  }

  async function handleDelete() {
    if (!selectedId || creating || !canDelete) return

    const guestName = form.name.trim() || 'this guest'
    if (
      !window.confirm(
        `Delete ${guestName}? The profile will be removed from guest lists. Past sales and visit history are kept.`,
      )
    ) {
      return
    }

    setSubmitting(true)
    setError('')

    try {
      await deleteCustomer(selectedId)
      navigate('/customers', { state: { success: 'Customer deleted.' } })
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Unable to delete customer.')
      setSubmitting(false)
    }
  }

  return (
    <div className="salon-page py-8">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <Button type="button" variant="ghost" className="mb-3 -ml-2 h-8 px-2" asChild>
            <Link to="/customers">
              <ArrowLeft className="mr-1.5 h-4 w-4" />
              Back to customers
            </Link>
          </Button>
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <Contact className="h-5 w-5" aria-hidden />
            </div>
            <div>
              <h1 className="text-xl font-semibold tracking-tight">
                {creating ? 'New customer' : 'Edit customer'}
              </h1>
              <p className="text-sm text-muted-foreground">
                {creating
                  ? 'Add a guest for bookings and checkout.'
                  : form.name.trim() || 'Update profile, visits, and packages.'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {success ? (
        <p className="mb-4 text-sm text-primary" role="status">
          {success}
        </p>
      ) : null}

      {error && activeTab !== 'profile' ? (
        <p className="mb-4 text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}

      <div className="card-surface overflow-hidden">
        {!creating && CUSTOMER_TABS.length > 1 ? (
          <div className="flex flex-wrap gap-2 border-b border-border/50 px-4 py-3 sm:px-6">
            {CUSTOMER_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'rounded-xl px-3 py-1.5 text-sm font-medium transition-colors',
                  activeTab === tab.id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted/70 text-foreground hover:bg-muted',
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
        ) : null}

        <div className="p-4 sm:p-6">
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading customer…</p>
          ) : activeTab === 'packages' && !creating && selectedId ? (
            <CustomerPackagesTab customerId={selectedId} />
          ) : activeTab === 'visits' && !creating && selectedId ? (
            <CustomerVisitHistoryTab customerId={selectedId} />
          ) : activeTab === 'photos' && !creating && selectedId ? (
            <div className="space-y-4">
              <form
                className="space-y-3 rounded-xl border border-border/70 p-3"
                onSubmit={async (event) => {
                  event.preventDefault()
                  const fileInput = event.currentTarget.elements.namedItem('photo')
                  const file = fileInput instanceof HTMLInputElement ? fileInput.files?.[0] : null
                  if (!file) return
                  setSubmitting(true)
                  setError('')
                  try {
                    const body = new FormData()
                    body.append('photo', file)
                    if (photoCaption.trim()) body.append('caption', photoCaption.trim())
                    await api.post(`/crm/customers/${selectedId}/photos`, body)
                    setPhotoCaption('')
                    if (fileInput instanceof HTMLInputElement) fileInput.value = ''
                    const { data } = await api.get(`/crm/customers/${selectedId}/photos`)
                    setPhotos(data?.data?.photos ?? [])
                  } catch (err) {
                    setError(apiErrorMessage(err, 'Unable to upload photo.'))
                  } finally {
                    setSubmitting(false)
                  }
                }}
              >
                <p className="text-sm font-medium">Upload photo</p>
                <input name="photo" type="file" accept="image/*" required className="text-sm" />
                <input
                  className={cn(
                    'h-10 w-full rounded-xl border border-border/80 bg-card px-3 text-sm',
                    'focus:outline-none focus:ring-2 focus:ring-ring',
                  )}
                  placeholder="Caption (optional)"
                  value={photoCaption}
                  onChange={(event) => setPhotoCaption(event.target.value)}
                />
                <Button type="submit" size="sm" disabled={submitting}>
                  Upload
                </Button>
              </form>
              {photosLoading ? (
                <p className="text-sm text-muted-foreground">Loading photos…</p>
              ) : (
                <ul className="grid gap-3 sm:grid-cols-2">
                  {photos.length === 0 ? (
                    <li className="text-sm text-muted-foreground">No photos yet.</li>
                  ) : (
                    photos.map((photo) => (
                      <li key={photo.id} className="rounded-xl border border-border/60 p-2">
                        {photo.url ? (
                          <img
                            src={photo.url}
                            alt={photo.caption || 'Customer photo'}
                            className="h-32 w-full rounded-lg object-cover"
                          />
                        ) : null}
                        <p className="mt-1 text-xs text-muted-foreground">
                          {photo.caption || photo.kind || 'Photo'}
                        </p>
                      </li>
                    ))
                  )}
                </ul>
              )}
            </div>
          ) : activeTab === 'recommendations' && !creating && selectedId ? (
            <div className="space-y-2">
              {recommendationsLoading ? (
                <p className="text-sm text-muted-foreground">Loading recommendations…</p>
              ) : recommendations.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No service history to recommend from yet.
                </p>
              ) : (
                recommendations.map((item) => (
                  <div
                    key={item.service_id}
                    className="rounded-xl border border-border/60 px-3 py-2 text-sm"
                  >
                    <p className="font-medium">{item.name}</p>
                    <p className="text-xs text-muted-foreground">
                      Rebook about every {item.rebook_after_days} days
                      {item.suggested_rebook_on ? ` · next ~ ${item.suggested_rebook_on}` : ''}
                    </p>
                  </div>
                ))
              )}
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="mx-auto max-w-3xl space-y-5">
              <CustomerFormFields
                form={form}
                stylists={stylists}
                creating={creating}
                onChange={updateField}
                onPhoneCountryChange={updatePhoneCountry}
                onPhoneLocalChange={(value) => updateField('phoneLocal', value)}
              />

              {error ? (
                <p className="text-sm text-destructive" role="alert">
                  {error}
                </p>
              ) : null}

              <div className="flex flex-wrap justify-between gap-2 border-t border-border/50 pt-4">
                {!creating && selectedId && canDelete ? (
                  <Button
                    type="button"
                    variant="outline"
                    disabled={submitting}
                    onClick={handleDelete}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete guest
                  </Button>
                ) : (
                  <span />
                )}
                <div className="flex gap-2">
                  <Button type="button" variant="outline" asChild disabled={submitting}>
                    <Link to="/customers">Cancel</Link>
                  </Button>
                  <Button type="submit" disabled={submitting}>
                    {submitting ? 'Saving…' : creating ? 'Create customer' : 'Save customer'}
                  </Button>
                </div>
              </div>
            </form>
          )}

          {!creating && selectedId && activeTab !== 'profile' ? (
            <div className="mt-5 flex flex-wrap justify-between gap-2 border-t border-border/50 pt-4">
              {canDelete ? (
                <Button
                  type="button"
                  variant="outline"
                  disabled={submitting}
                  onClick={handleDelete}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete guest
                </Button>
              ) : (
                <span />
              )}
              <Button type="button" variant="outline" asChild>
                <Link to="/customers">Close</Link>
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export function CustomerCreatePage() {
  return <CustomerFormPage mode="create" />
}

export function CustomerEditPage() {
  return <CustomerFormPage mode="edit" />
}
